import { Link } from 'react-router-dom';
import type { LitSquadCraftResult } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface CraftResultDialogProps {
  result: LitSquadCraftResult | null;
  open: boolean;
  onClose: () => void;
}

const RARITY_STYLES: Record<string, string> = {
  common: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-200',
  uncommon: 'bg-green-500/15 text-green-700 dark:text-green-300',
  rare: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  epic: 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  legendary: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
};

/**
 * Shown after a successful craft. Links to the new LitSquad inventory
 * item so the user can jump to it and see the full detail page.
 */
export function CraftResultDialog({ result, open, onClose }: CraftResultDialogProps) {
  if (!result) return null;
  const rarityClass = RARITY_STYLES[result.rarity] ?? RARITY_STYLES.common;
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>You crafted {result.displayName}!</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="w-40 h-40 rounded-lg bg-muted overflow-hidden">
            <img
              src={result.thumbnailUri}
              alt={result.displayName}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${rarityClass}`}>
              {result.rarity}
            </span>
            <span className="text-xs text-muted-foreground">{result.group}</span>
          </div>
        </div>
        <DialogFooter className="flex flex-row gap-2 sm:justify-between">
          <Button variant="outline" onClick={onClose}>
            Craft another
          </Button>
          <Link to={`/a/arche_litsquad/item/${result.itemId}`}>
            <Button>View in inventory</Button>
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
